
import React, { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

type PolicyVariant = 'default' | 'blue' | 'purple' | 'teal' | 'rose' | 'amber' | 'indigo' | 'cyan' | 'emerald';

interface PolicySectionCardProps {
  title: string;
  children: ReactNode;
  icon: LucideIcon;
  variant?: PolicyVariant;
  className?: string;
}

const variants: Record<PolicyVariant, string> = {
  default: "bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20",
  blue: "bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20",
  purple: "bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20",
  teal: "bg-gradient-to-br from-teal-500/10 to-teal-600/5 border-teal-500/20",
  rose: "bg-gradient-to-br from-rose-500/10 to-rose-600/5 border-rose-500/20",
  amber: "bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20",
  indigo: "bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border-indigo-500/20",
  cyan: "bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-cyan-500/20",
  emerald: "bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20",
};

const iconColors: Record<PolicyVariant, string> = {
  default: "text-primary",
  blue: "text-blue-600",
  purple: "text-purple-600",
  teal: "text-teal-600",
  rose: "text-rose-600",
  amber: "text-amber-600",
  indigo: "text-indigo-600",
  cyan: "text-cyan-600",
  emerald: "text-emerald-600",
};

const iconBgColors: Record<PolicyVariant, string> = {
  default: "bg-primary/10",
  blue: "bg-blue-500/10",
  purple: "bg-purple-500/10",
  teal: "bg-teal-500/10",
  rose: "bg-rose-500/10",
  amber: "bg-amber-500/10",
  indigo: "bg-indigo-500/10",
  cyan: "bg-cyan-500/10",
  emerald: "bg-emerald-500/10",
};

const PolicySectionCard: React.FC<PolicySectionCardProps> = ({ 
  title, 
  children, 
  icon: Icon, 
  variant = 'default',
  className
}) => {
  return (
    <motion.div
      className={cn("rounded-xl border shadow-sm overflow-hidden backdrop-blur-sm hover:shadow-md transition-all duration-300", variants[variant], className)}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div className={cn("p-2 rounded-lg mr-4", iconBgColors[variant])}>
            <Icon className={cn("h-5 w-5", iconColors[variant])} />
          </div>
          <h2 className={cn("text-xl font-semibold", iconColors[variant])}>{title}</h2>
        </div>
        <div className="text-gray-700 dark:text-gray-300 space-y-2">
          {children}
        </div>
      </div>
    </motion.div>
  );
};

export default PolicySectionCard;
